import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Separate interfaces for clear state/actions separation
interface ShellState {
  hideShell: boolean;
  /** Controls expanded sidebar overlay on mobile (<= 900px) */
  sidebarOpen: boolean;
  /** Desktop: true = sidebar hidden, false = expanded inline sidebar */
  sidebarCollapsed: boolean;
  /** Nav group names that are collapsed (all expanded by default) */
  collapsedGroups: string[];
}

interface ShellActions {
  toggleShell: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  toggleDesktopSidebar: () => void;
  toggleGroupCollapsed: (group: string) => void;
}

/**
 * Shell store for managing application shell visibility and sidebar state.
 */
export const useShellStore = create<ShellState & ShellActions>()(
  persist(
    set => ({
      // State
      hideShell: true,
      sidebarOpen: false,
      sidebarCollapsed: false,
      collapsedGroups: [],

      // Actions
      toggleShell: () => set(state => ({ hideShell: !state.hideShell })),
      openSidebar: () => set({ sidebarOpen: true }),
      closeSidebar: () => set({ sidebarOpen: false }),
      toggleSidebar: () => set(state => ({ sidebarOpen: !state.sidebarOpen })),
      toggleDesktopSidebar: () =>
        set(state => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      toggleGroupCollapsed: (group: string) =>
        set(state => ({
          collapsedGroups: state.collapsedGroups.includes(group)
            ? state.collapsedGroups.filter(g => g !== group)
            : [...state.collapsedGroups, group],
        })),
    }),
    {
      name: 'shell-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        hideShell: state.hideShell,
        sidebarCollapsed: state.sidebarCollapsed,
        collapsedGroups: state.collapsedGroups,
      }),
    }
  )
);
